<template>
  <v-container fluid class="discount-void-refund-page">
    <section>
<KpiCards class="mb-3" :items="kpiItems" :loading="!loadedSummary" :skeleton="3" />

      <!-- Filter Section -->
      <v-card class="br-panel dvr-filter-card mb-3" flat>
        <v-card-text class="py-3 px-4">
          <div class="d-flex align-center mb-3">
            <span class="br-accent-bar mr-2" />
            <h6 class="dvr-section-title mb-0">{{ $t("filter") }}</h6>
          </div>
          <v-row align="center" dense>
            <v-col cols="12" sm="4" md="3">
              <DatePicker
                :max="end_date"
                :dense="true"
                :placeholder="false"
                label="start_date"
                v-model="start_date"
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <DatePicker
                :min="start_date"
                :dense="true"
                :placeholder="false"
                label="end_date"
                v-model="end_date"
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4" md="2">
              <TextSelect
                :dense="true"
                :placeholder="false"
                :items="actionTypeList"
                label="action_type"
                itemValue="value"
                text="name"
                :returnObject="false"
                v-model="actionTypeFilter"
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="8" md="4" class="d-flex align-center">
              <TextSearch
                v-model="search"
                class="flex-grow-1"
                @search="filterData()"
                @onClear="
                  search = '';
                  filterData();
                "
              />
              <v-btn
                depressed
                outlined
                color="success"
                class="text-capitalize font-weight-bold rounded-lg ml-3 px-3"
                height="38"
                :title="$t('export_excel')"
                @click="handleExcelExport()"
              >
                <v-icon left small>fal fa-file-excel</v-icon>
                Excel
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Audit list -->
      <data-table
        v-if="reports && reports.data"
        :headers="headers"
        :items="reports"
        commit="SET_DISCOUNT_VOID_REFUND_REPORT"
      >
        <div slot="activator" class="row">
          <div class="col-12">
            <v-card-title class="pa-0 mb-4 d-flex align-center">
              <span class="br-accent-bar mr-2" />
              <span class="dvr-table-title">{{ $t("discount_void_refund_report") }}</span>
            </v-card-title>
          </div>
        </div>
      </data-table>

      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import DataTable from "@/components/DataTable";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { discountVoidRefundReportHead } from "@/utils/head";
import { myMixin } from "@/mixin/main";
import TextSelect from "@/components/global/TextSelect";
import TextSearch from "@/components/global/TextSearch";
import { toast, error } from "@/controllers/alert";

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

const monthAgo = new Date(
  Date.now() - new Date().getTimezoneOffset() * 60000 - 30 * 86400000
)
  .toISOString()
  .substr(0, 10);

export default {
  mixins: [myMixin],

  components: {
    DataTable,
    TextSelect,
    TextSearch,
  },

  data: () => ({
    search: "",
    summaries: {},
    loadedSummary: false,
    reports: null,
    start_date: monthAgo,
    end_date: today,
    actionTypeFilter: "all",
  }),

  async created() {
    this.SET_OVERLAY(true);
    try {
      await Promise.all([this.fetchSummary(), this.fetchItem()]);
    } finally {
      this.SET_OVERLAY(false);
    }
  },

  computed: {
    kpiItems() {
      return this.summaryTiles.map((t) => ({
        key: t.title,
        title: t.title,
        icon: t.icon,
        value: t.value,
        note: `${t.count} ${this.$t("items")}`,
      }));
    },

    ...mapGetters({
      overlay: "index/overlay",
    }),

    headers() {
      return discountVoidRefundReportHead(this);
    },

    byActionType() {
      return this.summaries.by_action_type || [];
    },

    summaryTiles() {
      const tiles = [
        { type: "discount", title: "discount", icon: "fal fa-tag" },
        { type: "cancel", title: "cancel", icon: "fal fa-ban" },
        { type: "refund", title: "refund", icon: "fal fa-undo" },
      ];
      return tiles.map((tile) => {
        const row = this.byActionType.find((r) => r.action_type === tile.type);
        return {
          title: tile.title,
          icon: tile.icon,
          value: (row && row.total_amount) || 0,
          count: (row && row.action_count) || 0,
        };
      });
    },

    actionTypeList() {
      return [
        { name: this.$t("all"), value: "all" },
        { name: this.$t("discount"), value: "discount" },
        { name: this.$t("cancel"), value: "cancel" },
        { name: this.$t("refund"), value: "refund" },
      ];
    },
  },

  watch: {
    start_date() {
      this.filterData();
    },
    end_date() {
      this.filterData();
    },
    actionTypeFilter() {
      this.filterData();
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),

    filterQuery() {
      let url = `start_date=${this.start_date}&end_date=${this.end_date}&q=${this.search}`;
      if (this.actionTypeFilter) {
        url += `&action_type=${this.actionTypeFilter}`;
      }
      return url;
    },

    async fetchSummary() {
      try {
        const { data } = await this.$http.get(
          `discount_void_refund_report_summary?${this.filterQuery()}`
        );
        if (data.status) {
          this.summaries = data.data;
        }
      } catch (err) {
        console.log("Error fetching discount/void/refund summary:", err);
      } finally {
        this.loadedSummary = true;
      }
    },

    async fetchItem() {
      await this.fetch({
        that: this,
        url: `discount_void_refund_report?${this.filterQuery()}`,
        commit: "SET_DISCOUNT_VOID_REFUND_REPORT",
      });
      this.reports = this.$store.getters["index/discountVoidRefundReports"];
    },

    async filterData() {
      this.SET_OVERLAY(true);
      try {
        await Help.getDifferentMonth(this, this.start_date, this.end_date);
        await Promise.all([this.fetchSummary(), this.fetchItem()]);
      } catch (err) {
        console.log("error", err.message);
        error(this, err.message);
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    async handleExcelExport() {
      this.SET_OVERLAY(true);
      try {
        const data = await this.$http.get(
          `download_discount_void_refund_report?${this.filterQuery()}&locale=${
            this.$i18n.locale
          }`,
          { responseType: "blob" }
        );

        const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute(
          "download",
          `discount_void_refund_report_${this.start_date}_to_${this.end_date}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        toast(this, "excel_export_success");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        error(this, "excel_export_failed");
      } finally {
        this.SET_OVERLAY(false);
      }
    },
  },
};
</script>

<style scoped>
.discount-void-refund-page .dvr-filter-card,
.discount-void-refund-page ::v-deep .ps-datatable-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  background: #ffffff !important;
}

.theme--dark .discount-void-refund-page .dvr-filter-card,
.theme--dark .discount-void-refund-page ::v-deep .ps-datatable-card {
  border-color: rgba(154, 140, 240, 0.16) !important;
  background: #1b1d30 !important;
  box-shadow: none !important;
}

.dvr-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #141021;
}

.dvr-table-title {
  font-size: 16px;
  font-weight: 700;
  color: #141021;
}

.theme--dark .dvr-section-title,
.theme--dark .dvr-table-title {
  color: #eef0fa;
}
</style>
